import { Box, Card, Divider, Grid, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import IconContentAdd from '@material-ui/icons/Add'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import EditIcon from '@material-ui/icons/Edit'
import VisibilityIcon from '@material-ui/icons/Visibility'
import WarningIcon from '@material-ui/icons/Warning'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { filter, find, get } from 'lodash'
import { DateTime } from 'luxon'
import { useLoading } from 'ra-core'
import { cloneElement, useEffect, useState } from 'react'
import {
  ArrayField,
  Button,
  Datagrid,
  DateField,
  EditButton,
  FileField,
  FunctionField,
  Identifier,
  Labeled,
  NumberField,
  ReferenceField,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useNotify,
  usePermissions,
  useRedirect,
  useRefresh,
  useShowController,
} from 'react-admin'
import { Divider as SharedDivider, InlineBlock } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Grid as SharedGrid } from 'shared/components/Base/Grid'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { Notes } from 'shared/components/Notes/Notes'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { Text } from 'shared/components/Text/Text'
import { getAppUrl } from 'shared/config/config'
import { lightgreye3 } from 'shared/theme/colors'
import styled from 'styled-components'

import { sendEdiInvoice, updateInvoiceParsingStatus } from 'api/invoices'
import { AutoBidInfo } from 'components/Common/AutoBidInfo'
import { BrokerCreditRatingLabel } from 'components/Common/BrokerCreditRatingLabel'
import { RiskyDriverLabel } from 'components/Common/RiskyDriver'
import { CopyToClipboardButton } from 'components/CopyToClipboardButton/CopyToClipboardButton'
import { formatTripRate, jobHasUploadedRateCon } from 'helpers/jobs'
import {
  captureExceptionIfProduction,
  getFormattedDate,
  getPaymentPolicyLinkForId,
  getUserFullName,
} from 'helpers/utils'
import {
  useHasConsoleJobDetailsRedesign,
  useHasEnabledFeasibilityCheckForVCUsers,
} from 'hooks/useHasFeatureFlag'
import { BrokerCategoryKey, BrokerCategoryName } from '../../../src/models/jobs'
import { getBrokerNoaAcknowledgments } from '../../api/core'
import { getJobBids, reviewForApproval } from '../../api/jobs'
import {
  BrokerSubmissionMethodMap,
  BrokerSubmissionMethods,
  FSM_TRANSITION_RECORD_TYPE,
  INVOICE_STATUS,
  JobStatus,
  JobStatusDetail,
  RATE_CON_CONFIRMED_JOB_STATUS_DETAILS,
  StatementType,
  SUPPORTED_EDI_INVOICE_SHIPPER_FEINS,
} from '../../helpers/constants'
import {
  Job,
  ParsingStatus,
  ParsingStatusChoicesMap,
  RateConParsedStatus,
  ReviewForApprovalResponse,
  TruckercloudIntegrationStatusEnum,
} from '../../models/jobs'
import { ProductSubscription } from '../../models/subscription'
import { hasPermission, Permissions } from '../../Permissions'
import Aside from '../Aside/aside'
import { BrokerStatus, REASONS_REQUIRED_BROKER_STATUSES } from '../Brokers/BrokerStatusTransitions'
import { BaseActionDetailButton } from '../Common/ActionButtons'
import { CustomReferenceField } from '../Common/CustomReferenceFields'
import { EarlyLifecycleDriverLabel } from '../Common/EarlyLifecycleDriver'
import { ELDIntegrationField } from '../Common/ELDVerification'
import { CloneLoadButton, LoadStops } from '../Common/Loads'
import { ProductChip } from '../Common/ProductChip'
import { CorrectionFinalizeButton, TotalCashAdvancePayments } from '../Common/Statements'
import { ConsoleUserProvider } from '../Context/ConsoleUserProvider'
import {
  CalloutField,
  CurrencyField,
  CustomBooleanField,
  CustomDateField,
  CustomLinkifiedField,
  GenericFormField,
  LabeledTypographyField,
  OriginDestinationField,
} from '../Fields'
import { FSMButtonToolbar } from '../FSM/Buttons/FSMTransitionButton'
import { IssueSectionV2 } from '../Issues/IssueSectionV2'
import { TeamLink } from '../Teams/TeamLink'
import { UserLink } from '../User/UserLink'
import AutoApprovalFailureFlags from './AutoApprovalFailureFlags'
import { ClickToCopyJobInfo } from './ClickToCopyJobInfo'
import { JobBidModal } from './JobBidModal'
import { EditAssignedDriverButton } from './JobDetails/EditAssignedDriverButton'
import { EditAssignedTruckButton } from './JobDetails/EditAssignedTruckButton'
import { JobDetailsLoadInfo } from './JobDetails/JobDetailsLoadInfo'
import { JobDetailsStatusDetail } from './JobDetails/JobDetailsStatusDetail'
import { JobDetailsStatusDetailV2 } from './JobDetails/JobDetailsStatusDetailV2'
import { JobDetailsStops } from './JobDetails/JobDetailsStops'
import { JobDetailsTopInfo } from './JobDetails/JobDetailsTopInfo'
import { JobDriverCard } from './JobDetails/JobDriverCard'
import { JobTracking, JobTrackingCard } from './JobTracking'
import { JobZendeskTickets } from './JobZendeskTickets'
import TruckercloudRouteProgressSection from './TruckerCloudTracking'
import { UploadDocumentButton, UploadRateConButton } from './UploadDocument'

const styles = {
  root: {
    background: 'crimson',
  },
  completed: {
    color: 'green',
  },
  clickToCopyButton: {
    color: '#0000ee',
    padding: 0,
    '& span': {
      padding: 0,
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  progress: {
    color: 'orange',
  },
  pointerCursor: {
    cursor: 'pointer',
  },
  lateCancellationRequest: {
    color: 'red',
  },
  shareLocationMenu: {
    '& ul': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
}

/*
 Here we need to restrict access to job transitions, and specifically check for the IssuePayment
 transition because it is treated separately from updating a job.
*/
const JobToolbar = ({ record, permissions, classes, isLoading }: any) => (
  <FSMButtonToolbar
    record={record}
    recordType={FSM_TRANSITION_RECORD_TYPE.job}
    classes={classes}
    isLoading={isLoading}
    isHidden={
      !hasPermission(permissions, Permissions.ChangeJob) ||
      (record.status_detail === JobStatusDetail.PaymentRequired &&
        !hasPermission(permissions, Permissions.IssuePaymentStatement))
    }
  />
)

function onClickApproveJobWithDiscrepancies({ notify, redirect, record, basePath }: any) {
  reviewForApproval(record.id)
    .then((response) => {
      if (response.data.status === RateConParsedStatus.PENDING) {
        notify('Rate con parsing is still being processed. Please retry in a minute.', {
          type: 'info',
        })
      } else {
        redirect(`${basePath}/${record.id}/approve-v2`, undefined, undefined, undefined, {
          reviewForApprovalResponse: response.data,
        })
      }
    })
    .catch((error) => {
      captureExceptionIfProduction(error)
      notify('An unexpected error occurred. Please try again.', {
        type: 'error',
      })
    })
}

const ApproveJobButton = ({ record, basePath }: any) => {
  const notify = useNotify()
  const redirect = useRedirect()
  const isButtonDisabled = record?.is_console_approve_action_disabled
  return record.status === JobStatus.Pending ? (
    <Button
      variant="contained"
      color="primary"
      label="Approve job"
      basePath={basePath}
      disabled={isButtonDisabled}
      onClick={() => {
        onClickApproveJobWithDiscrepancies({ notify, redirect, record, basePath })
      }}
    />
  ) : null
}

const CorrectPaymentButton = ({ record, basePath, classes }: any) => {
  const redirect = useRedirect()
  const isCancelled = record?.status_detail === JobStatusDetail.Canceled
  return RATE_CON_CONFIRMED_JOB_STATUS_DETAILS.includes(record.status_detail) ? (
    <Button
      className={classes.root}
      variant="contained"
      color="primary"
      label={isCancelled ? 'Payment Correction Disabled (Cancelled Job)' : 'Make Payment Correction'}
      disabled={isCancelled}
      basePath={basePath}
      onClick={() => redirect(`${basePath}/${record.id}/correction`)}
    />
  ) : null
}

const AddAccessorialStatementButton = ({ record, basePath }: any) => {
  const redirect = useRedirect()
  const isCancelled = record?.status_detail === JobStatusDetail.Canceled
  return RATE_CON_CONFIRMED_JOB_STATUS_DETAILS.includes(record.status_detail) ? (
    <Button
      variant="contained"
      color="primary"
      label={isCancelled ? 'Accessorial Statement Disabled (Cancelled Job)' : 'Add Accessorial Statement'}
      disabled={isCancelled}
      basePath={basePath}
      onClick={() => redirect(`${basePath}/${record.id}/correction?isAccessorial=true`)}
    />
  ) : null
}

const JobShowActions = ({ basePath, data, permissions, ...rest }: any) => {
  // TODO probably should disable all buttons here and show spinner while loading?
  const isLoading = useLoading()

  if (!data || !permissions) {
    return null
  }

  const canChangeJob = hasPermission(permissions, Permissions.ChangeJob)
  const canCreateStatement = hasPermission(permissions, Permissions.AddStatement)
  const canCreateLoad = hasPermission(permissions, Permissions.AddLoad)

  const load = data.load

  const invoices = get(data, 'invoices', [])
  const outstandingInvoices = filter(invoices, (invoice) =>
    [INVOICE_STATUS.draft, INVOICE_STATUS.open].includes(invoice.status)
  )

  return (
    <TopToolbar>
      <Grid container direction="row" spacing={2} justifyContent="flex-start">
        {data?.status === JobStatus.Pending && !jobHasUploadedRateCon(data) && (
          <Grid item>
            <UploadRateConButton record={data} permissions={permissions} />
          </Grid>
        )}
        {canChangeJob && (
          <Grid item>
            <ApproveJobButton basePath={basePath} record={data} />
          </Grid>
        )}
        {canChangeJob && (
          <Grid item>
            <EditButton basePath={basePath} record={data} />
          </Grid>
        )}
        {canCreateStatement && (
          <Grid item>
            <CorrectPaymentButton basePath={basePath} record={data} {...rest} />
          </Grid>
        )}
        {canCreateStatement && (
          <Grid item>
            <AddAccessorialStatementButton basePath={basePath} record={data} {...rest} />
          </Grid>
        )}
        {canCreateLoad && (
          <Grid item>
            <CloneLoadButton
              record={load}
              showWarning={outstandingInvoices.length > 0}
              disabled={isLoading}
            />
          </Grid>
        )}
      </Grid>
    </TopToolbar>
  )
}

interface BrokerFieldProps {
  record?: any
  isCashTeam?: boolean
  noaAcknowledgment?: any
}

const BrokerField = ({ record = {}, isCashTeam, noaAcknowledgment }: BrokerFieldProps) => {
  const broker = record.load.broker
  const statusIcon =
    broker.cloudtrucks_llc_status === BrokerStatus.Approved.id ? CheckCircleIcon : WarningIcon
  const statusColor =
    broker.cloudtrucks_llc_status === BrokerStatus.Approved.id
      ? 'green'
      : REASONS_REQUIRED_BROKER_STATUSES.includes(broker.cloudtrucks_llc_status)
      ? 'red'
      : 'orange'
  const carrierSetupCompleteIcon = broker.is_carrier_packet_complete ? CheckCircleIcon : WarningIcon
  const carrierSetupCompleteColor = broker.is_carrier_packet_complete ? 'green' : 'red'
  const carrierSetupCompleteLabel = broker.is_carrier_packet_complete ? 'Completed' : 'Not Completed'
  const isNoaAcknowledged = get(noaAcknowledgment, 'is_acknowledged')
  return (
    <div>
      <Grid item container direction="column" spacing={0}>
        <Grid item>
          <Labeled label="Broker">
            <CustomReferenceField
              baseUrl="/brokers"
              record={record}
              source="load.broker"
              representationFunction={(broker: any) => broker.name}
            />
          </Labeled>
          <BrokerCreditRatingLabel
            creditRating={broker?.credit_rating}
            creditRatingCategory={broker?.credit_rating_category[BrokerCategoryKey.CREDIT_RATING]}
            creditRatingCategoryName={BrokerCategoryName.CREDIT_RATING}
            paddingScore={'10px'}
            paddingName={'10px'}
          />
        </Grid>
        <Grid item>
          <CalloutField
            icon={statusIcon}
            iconProps={{ style: { color: statusColor, fontSize: 'medium' } }}
            label={broker.cloudtrucks_llc_status}
            labelVariant="body2"
          />
        </Grid>
        {isCashTeam && (
          <Grid item>
            <Labeled label="Notice of Assignment (NOA)">
              <CalloutField
                icon={isNoaAcknowledged ? CheckCircleIcon : WarningIcon}
                iconProps={{ style: { color: isNoaAcknowledged ? 'green' : 'red', fontSize: 'medium' } }}
                label={isNoaAcknowledged ? 'Acknowledged' : 'Not Acknowledged'}
                labelVariant="body2"
              />
            </Labeled>
          </Grid>
        )}

        <Grid item>
          <Labeled label="Carrier Setup Completed">
            <CalloutField
              icon={carrierSetupCompleteIcon}
              iconProps={{ style: { color: carrierSetupCompleteColor, fontSize: 'medium' } }}
              label={carrierSetupCompleteLabel}
              labelVariant="body2"
            />
          </Labeled>
        </Grid>
      </Grid>
    </div>
  )
}

const CustomJobShow = (props: any) => {
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    resource, // the resource name, deduced from the location. e.g. 'posts'
    version, // integer used by the refresh feature
  } = useShowController(props)
  const hasConsoleJobDetailsRedesignFlag = useHasConsoleJobDetailsRedesign()
  const [useNewJobDetails, setUseNewJobDetails] = useState(hasConsoleJobDetailsRedesignFlag)

  useEffect(() => {
    setUseNewJobDetails(hasConsoleJobDetailsRedesignFlag)
  }, [hasConsoleJobDetailsRedesignFlag])

  function renderNewJobDetailsSwitch() {
    if (!hasConsoleJobDetailsRedesignFlag) return null

    return (
      <InlineBlock className="job-details-page-switch">
        <FormControlLabel
          control={
            <Switch checked={useNewJobDetails} onChange={(e) => setUseNewJobDetails(e.target.checked)} />
          }
          label={
            <Text variant="header4" mt={0.5}>
              Show new job details page
            </Text>
          }
        />
      </InlineBlock>
    )
  }

  const [bids, setBids] = useState<any[]>([])
  const [latestBid, setLatestBid] = useState<any>(null)
  const [noaAcknowledgment, setNoaAcknowledgment] = useState<any>(null)
  const [reviewForApprovalResponse, setReviewForApprovalResponse] =
    useState<ReviewForApprovalResponse | null>(null)

  const contactedLoad = get(record, 'contacted_load')
  const bidHistory = get(contactedLoad, 'bid_history')
  const bidMinimum = get(contactedLoad, 'bid_minimum')
  const bidMaximum = get(contactedLoad, 'bid_maximum')
  const { permissions } = usePermissions()

  const isCashTeam = record?.team?.product_subscription_name === ProductSubscription.CASH

  usePageTitle(`Job ${record?.human_id || ''}`)

  // TODO will we be deprecating manually placed bids?
  useEffect(() => {
    if (record && record.id) {
      getJobBids(record.id).then((response) => {
        setBids(response)
      })
    }
  }, [record])

  useEffect(() => {
    if (bids) {
      // API returns bids in descending date_created order (newest first)
      setLatestBid(bids[0])
    }
  }, [bids])

  useEffect(() => {
    if (record && record.id && isCashTeam) {
      getBrokerNoaAcknowledgments(record?.team?.id).then((response) => {
        const allAcks = response.data
        const brokerAck = find(allAcks, (ack) => ack.broker.id === record?.load?.broker?.id)
        if (brokerAck) {
          setNoaAcknowledgment(brokerAck)
        }
      })
    }
  }, [record, isCashTeam])

  useEffect(() => {
    if (
      record &&
      record.id &&
      record?.status_detail === JobStatusDetail.ReviewingRateConfirmation &&
      jobHasUploadedRateCon(record as Job)
    ) {
      reviewForApproval(record?.id).then((response) => {
        const reviewForApprovalResponse = response.data as ReviewForApprovalResponse
        setReviewForApprovalResponse(reviewForApprovalResponse)
      })
    }
  }, [record])

  const showBidInfo = latestBid && record?.status_detail === JobStatusDetail.ActiveNegotiation

  function renderBidInfo() {
    if (!showBidInfo) return null
    return (
      <div>
        ${latestBid.amount}, {latestBid.created_by.email} at {getFormattedDate(latestBid.date_created)}{' '}
        <br></br> {latestBid.notes}
      </div>
    )
  }

  const truckercloud_status = record?.truckercloud_verification_status
  const hide_truckercloud_route_progress = [TruckercloudIntegrationStatusEnum.NOT_APPLICABLE].includes(
    truckercloud_status
  )
  const isFeasibilityCheckEnabled = useHasEnabledFeasibilityCheckForVCUsers()
  const isActiveNegotiation = record?.status_detail === JobStatusDetail.ActiveNegotiation
  const hasBidHistory = bidHistory && bidHistory.length > 0
  const showJobTrackingCard =
    isFeasibilityCheckEnabled && record?.status_detail === JobStatusDetail.InProgress

  const { classes } = props // classes for custom styling
  return loaded && record?.id ? (
    <div style={{ maxWidth: '1865px' }}>
      {renderNewJobDetailsSwitch()}
      <ConsoleUserProvider
        isPremium={false}
        isFlexStarter={false}
        hasStarterFeature={false}
        hasFactoringFeature={false}
        permissions={permissions}
      >
        <Notes
          recordId={record.id as string}
          recordType="jobs"
          recordName={record.human_id as string}
          expandByDefault={false}
        >
          {(renderOpenNotesCard) => (
            <Grid container direction="column" spacing={3} alignItems="baseline" justifyContent="flex-start">
              {reviewForApprovalResponse?.auto_approval_failure_flags && (
                <Grid item>
                  <AutoApprovalFailureFlags
                    reviewForApprovalResponse={reviewForApprovalResponse}
                    job={record as Job}
                  />
                </Grid>
              )}
              <Grid item>
                <JobShowActions basePath={basePath} data={record} {...props} />
              </Grid>

              {useNewJobDetails && (
                <Grid item style={{ width: '100%' }}>
                  <SharedGrid gap="60px" columns="1fr 300px" style={{ maxWidth: 1128 }}>
                    <FlexBox isColumn gap="20px">
                      <JobDetailsTopInfo job={record as Job} />

                      <SharedDivider color={lightgreye3} />

                      <SharedGrid gap="20px" columns="1fr auto 1fr">
                        <FlexBox isColumn gap="32px" justifyContent="flex-start" flex={1}>
                          <JobDetailsStops job={record as Job} />
                          <JobDetailsStatusDetailV2 job={record as Job} />
                        </FlexBox>

                        <SharedDivider vertical color={lightgreye3} />

                        <JobDetailsLoadInfo job={record as Job} bids={bids} setBids={setBids} />
                      </SharedGrid>

                      <FlexBox justifyContent="flex-start">
                        {showJobTrackingCard && <JobTrackingCard job={record as Job} />}
                      </FlexBox>
                    </FlexBox>

                    <FlexBox isColumn gap="24px">
                      <JobDriverCard job={record as Job} />
                      {renderOpenNotesCard()}
                    </FlexBox>
                  </SharedGrid>
                </Grid>
              )}

              {!useNewJobDetails && (
                <>
                  <Grid item>
                    <Grid item container direction="column" justifyContent="flex-start">
                      <Grid item>
                        <Typography variant="h4">
                          Job: {record?.human_id}
                          <ProductChip record={record} />
                        </Typography>
                        {record?.is_early_cycle_driver && <EarlyLifecycleDriverLabel />}
                        {record?.is_risky && <RiskyDriverLabel />}
                      </Grid>
                      <Box>
                        <ClickToCopyJobInfo record={record as Job}>
                          <div>
                            <Button
                              variant="text"
                              color="primary"
                              label="Copy Driver Contact & ELD"
                              className={classes.clickToCopyButton}
                            />
                          </div>
                        </ClickToCopyJobInfo>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid item container direction="row" alignItems="stretch" spacing={0}>
                    <Grid
                      item
                      container
                      direction="row"
                      alignItems="stretch"
                      justifyContent="flex-start"
                      spacing={2}
                      xs={5}
                    >
                      <Grid item container direction="column" alignItems="stretch" spacing={0} xs={6}>
                        <JobDetailsStatusDetail
                          record={record as Job}
                          classes={classes}
                          reviewForApprovalResponse={reviewForApprovalResponse}
                        />
                        <Grid item container direction="column" justifyContent="flex-start">
                          <Grid item container direction="row" justifyContent="flex-start">
                            <Labeled label="Assigned Driver">
                              <FlexBox alignItems="center">
                                <UserLink user={record.assigned_to} />
                                <EditAssignedDriverButton job={record as Job} />
                              </FlexBox>
                            </Labeled>
                          </Grid>
                          <Grid item>
                            <LabeledTypographyField
                              variant="body2"
                              label="Mobile"
                              value={record?.assigned_to?.mobile}
                            />
                          </Grid>
                          <Grid item>
                            <LabeledTypographyField
                              variant="body2"
                              label="Email"
                              value={record?.assigned_to?.email}
                            />
                          </Grid>
                          <Grid item>
                            <Labeled label="Team (Product)">
                              <TeamLink
                                team={{
                                  id: record.team?.id,
                                  name: record.team?.name,
                                  productSubscriptionName: record.team?.formatted_subscription_name,
                                }}
                              />
                            </Labeled>
                          </Grid>

                          <Grid item>
                            <Labeled label="Assigned truck">
                              <div>
                                <FlexBox alignItems="center">
                                  <CustomReferenceField
                                    record={record}
                                    source="team"
                                    sortable={false}
                                    label="Assigned truck"
                                    baseUrl="/teams"
                                    appendUrl="/equipment"
                                    representationFunction={() => {
                                      return (
                                        <span
                                          style={{
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            maxWidth: '100%',
                                            display: 'inline-block',
                                          }}
                                        >
                                          {[
                                            record?.assigned_truck?.vehicle_year,
                                            record?.assigned_truck?.vehicle_make,
                                            record?.assigned_truck?.vehicle_model,
                                            record?.assigned_truck?.vin_number,
                                          ]
                                            .filter((x) => !!x)
                                            .join(' ')
                                            .trim() || 'No truck assigned'}
                                        </span>
                                      )
                                    }}
                                  />
                                  <EditAssignedTruckButton job={record as Job} />
                                </FlexBox>

                                {!!record?.assigned_truck?.eld_vehicles[0] && (
                                  <ExternalLink
                                    variant="header3"
                                    href={record?.assigned_truck?.eld_vehicles[0]?.provider_url}
                                  >
                                    {record?.assigned_truck?.eld_vehicles
                                      ? `Motive ID ${record?.assigned_truck?.eld_vehicles[0]?.provider_name}`
                                      : ''}
                                  </ExternalLink>
                                )}
                              </div>
                            </Labeled>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item container direction="column" xs={6}>
                        <Grid item>
                          <Labeled label="Origin &rarr; Destination">
                            <Typography variant="subtitle1">
                              <OriginDestinationField record={record?.load} />
                            </Typography>
                          </Labeled>
                        </Grid>
                        <Grid item container spacing={4}>
                          <Grid item>
                            <LabeledTypographyField
                              variant="body1"
                              label="Source"
                              value={record?.load.source}
                            />
                          </Grid>
                          <Grid item>
                            <LabeledTypographyField
                              variant="body1"
                              label="Broker load ID"
                              value={record?.load.source_human_id}
                            />
                          </Grid>
                        </Grid>
                        <Grid item>
                          {record?.load.broker && (
                            <Grid item container>
                              <BrokerField
                                record={record}
                                isCashTeam={isCashTeam}
                                noaAcknowledgment={noaAcknowledgment}
                              />
                            </Grid>
                          )}
                          {!hide_truckercloud_route_progress && (
                            <Grid item>
                              <ELDIntegrationField status={record?.truckercloud_verification_status} />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {(isActiveNegotiation || hasBidHistory) && (
                      <Grid item container direction="column" xs={4}>
                        <Box mr={2}>
                          {isActiveNegotiation && (
                            <>
                              <Grid item>
                                {latestBid && (
                                  <LabeledTypographyField
                                    variant="body1"
                                    label="Latest Bid Placed"
                                    value={renderBidInfo()}
                                  />
                                )}
                              </Grid>
                              <Grid item>
                                <JobBidModal
                                  record={record}
                                  bids={bids}
                                  setBids={setBids}
                                  className={classes.clickToCopyButton}
                                />
                              </Grid>
                            </>
                          )}
                          {hasBidHistory && (
                            <AutoBidInfo
                              history={bidHistory}
                              bidMinimum={bidMinimum}
                              bidMaximum={bidMaximum}
                            />
                          )}
                        </Box>
                      </Grid>
                    )}

                    <Grid item container xs={3}>
                      <style>{`.notes-card { width: 100%; max-width: 100%; }`}</style>
                      <FlexBox
                        gap="20px"
                        alignItems="flex-start"
                        justifyContent="flex-start"
                        isColumn
                        isFullWidth
                        ml={2}
                      >
                        {showJobTrackingCard && <JobTrackingCard job={record as Job} />}
                        {renderOpenNotesCard()}
                      </FlexBox>
                    </Grid>
                  </Grid>
                </>
              )}
              <Grid item>
                <Card style={{ marginTop: '20px', paddingBottom: '20px' }}>
                  {cloneElement(props.children, {
                    basePath,
                    record,
                    resource,
                    version,
                  })}
                </Card>
              </Grid>
              {record?.is_factored && (
                <Grid item>
                  <JobToolbar record={record} {...props} isLoading={!loaded} />
                </Grid>
              )}
              <Grid item>
                <Aside record={record} resource="jobs" />
              </Grid>
            </Grid>
          )}
        </Notes>
      </ConsoleUserProvider>
    </div>
  ) : null
}

const GenerateInvoiceButton = (props: any) => {
  const { redirect, record } = props
  const shouldDisplayButton = RATE_CON_CONFIRMED_JOB_STATUS_DETAILS.includes(record.status_detail)
  if (!shouldDisplayButton) {
    return (
      <Typography variant="body1" component="span">
        Job delivery must be verified to create an invoice.
      </Typography>
    )
  }

  if (record?.status_detail === JobStatusDetail.Canceled) {
    return <Typography>Invoices can no longer be created because this job has been cancelled.</Typography>
  }

  return (
    <Button label="New invoice" basePath="/jobs" onClick={() => redirect(`/jobs/${record.id}/invoice`)}>
      <IconContentAdd />
    </Button>
  )
}

const TrackingSection = (props: any) => {
  const { record } = props
  const truckercloud_status = record.truckercloud_verification_status
  const show_truckercloud_route_progress =
    truckercloud_status && truckercloud_status !== TruckercloudIntegrationStatusEnum.NOT_APPLICABLE
  // public tracking is available if the load is in progress or has been completed in the last two weeks
  const trackingAvailable = [JobStatus.Current, JobStatus.Past].includes(record.status)

  const destDeliveryDate = DateTime.fromISO(record.load.dest_delivery_date)
  const twoWeeksAgo = DateTime.local().minus({ weeks: 2 })

  const brokerLoadId = record.load.source_human_id
  const showPublicTracking = trackingAvailable && destDeliveryDate >= twoWeeksAgo && !!brokerLoadId
  const hasELDLocationTracking =
    record.eld_load_stop_progresses &&
    record.eld_load_stop_progresses.length > 0 &&
    record.tracking_current_eld_load_stop_progress &&
    record.tracking_current_eld_load_stop_progress.tracking_last_location_vector
  const hasMobileLocationTracking =
    record.load_stop_progresses &&
    record.load_stop_progresses.length > 0 &&
    record.tracking_current_load_stop_progress &&
    record.tracking_current_load_stop_progress.tracking_last_location_vector

  const isFeasibilityCheckEnabled = useHasEnabledFeasibilityCheckForVCUsers()

  return (
    <div style={{ margin: '20px' }}>
      <Grid container direction="column">
        {show_truckercloud_route_progress && (
          <Grid container item>
            <TruckercloudRouteProgressSection {...props} />
          </Grid>
        )}
        {show_truckercloud_route_progress && <Divider style={{ marginTop: '40px', marginBottom: '40px' }} />}
        <Grid item container direction="row" justifyContent="flex-end">
          {showPublicTracking &&
            (hasELDLocationTracking ? (
              <CopyToClipboardButton
                text={`${getAppUrl()}/job-tracking/${brokerLoadId}?isMobileTracking=${false}`}
                label="Share ELD Tracking Link"
                styles="margin: 5px;"
              />
            ) : hasMobileLocationTracking ? (
              <CopyToClipboardButton
                text={`${getAppUrl()}/job-tracking/${brokerLoadId}?isMobileTracking=${true}`}
                label="Share Mobile Tracking Link"
                styles="margin: 5px;"
              />
            ) : null)}
        </Grid>
        <Grid item>
          <JobTracking
            isMobile={false}
            record={record}
            loadStopProgresses={record.eld_load_stop_progresses}
            trackingCurrentLoadStopProgress={record.tracking_current_eld_load_stop_progress}
            trackingStatus={
              isFeasibilityCheckEnabled && record.tracking_current_eld_load_stop_progress?.last_schedule_event
                ? record.hos_eld_tracking_status
                : record.eld_tracking_status
            }
            showHosEta={isFeasibilityCheckEnabled}
          />
        </Grid>
        <Divider style={{ marginTop: '40px', marginBottom: '40px' }} />
        <Grid item>
          <JobTracking
            isMobile={true}
            record={record}
            loadStopProgresses={record.load_stop_progresses}
            trackingCurrentLoadStopProgress={record.tracking_current_load_stop_progress}
            trackingStatus={
              isFeasibilityCheckEnabled && record.tracking_current_load_stop_progress?.last_schedule_event
                ? record.hos_mobile_tracking_status
                : record.tracking_status
            }
            showHosEta={isFeasibilityCheckEnabled}
          />
        </Grid>
      </Grid>
    </div>
  )
}

const InvoiceSection = (props: any) => {
  const redirect = useRedirect()
  const refresh = useRefresh()
  const notify = useNotify()

  const { record, permissions, classes } = props
  if (!record || !permissions || !record.invoices) {
    return null
  }
  const hasAddPermissions = hasPermission(permissions, Permissions.AddInvoice)
  const hasEditPermissions = hasPermission(permissions, Permissions.ChangeInvoice)

  const sendEdiInvoiceTitle = 'Send EDI Invoice'
  const sendEdiInvoiceBody =
    'This will send an EDI Invoice to the shipper. Are you sure you want to continue?'
  const sendEdiInvoiceActionRequest = async (invoiceId: Identifier) => {
    await sendEdiInvoice(invoiceId, record.id)
  }
  const updateInvoiceParsingStatusRequest = async (invoiceId: Identifier, ar_parsing_status: string) => {
    try {
      await updateInvoiceParsingStatus(invoiceId, ar_parsing_status)
      refresh()
    } catch (error: any) {
      notify(error.message, { type: 'error' })
    }
  }

  const invoices = record.invoices.map((invoice: any) => {
    return (
      <div style={{ padding: '30px', marginBottom: '30px' }}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
          <Grid
            item
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={3}
          >
            {invoice.last_auto_email_to_broker_at && (
              <Grid item>
                <Labeled label="Auto Sent to Broker">
                  <>✅</>
                </Labeled>
              </Grid>
            )}
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={8}
            >
              <Grid item>
                <Labeled label="Invoice #">
                  <TextField record={invoice} source="human_id" />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Invoice date">
                  <CustomDateField record={invoice} source="date_invoiced" />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Due date">
                  <CustomDateField record={invoice} source="date_due" label="Due Date" />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Broker">
                  <CustomReferenceField
                    baseUrl="/brokers"
                    record={invoice}
                    source="broker"
                    representationFunction={(broker: any) => broker.name}
                  />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="PDF file">
                  <FileField record={invoice} source="pdf" title="View file" />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Created by">
                  <FunctionField
                    record={invoice}
                    render={(record: any) => (
                      <UserLink user={record.created_by}>{getUserFullName(record.created_by)}</UserLink>
                    )}
                  />
                </Labeled>
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={8}
            >
              <Grid item>
                <Labeled label="Status">
                  <TextField record={invoice} source="status" />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Payment applied">
                  <CustomDateField
                    record={invoice}
                    source="date_payment_applied"
                    label="Payment applied date"
                  />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Parsing Status">
                  <FunctionField
                    source="ar_parsing_status"
                    record={invoice}
                    render={(record: any) => (
                      <ParsingStatusDropdown
                        width={180}
                        isFullWidth
                        noMargin
                        value={record.ar_parsing_status}
                        options={[
                          {
                            label: ParsingStatusChoicesMap[ParsingStatus.ACCURATELY_PARSED],
                            value: ParsingStatus.ACCURATELY_PARSED,
                          },
                          {
                            label: ParsingStatusChoicesMap[ParsingStatus.INACCURATELY_PARSED],
                            value: ParsingStatus.INACCURATELY_PARSED,
                          },
                          {
                            label: ParsingStatusChoicesMap[ParsingStatus.UNREVIEWED],
                            value: ParsingStatus.UNREVIEWED,
                          },
                          {
                            label: ParsingStatusChoicesMap[ParsingStatus.NOT_PARSED],
                            value: ParsingStatus.NOT_PARSED,
                          },
                        ]}
                        onChange={(value: any) => {
                          updateInvoiceParsingStatusRequest(record.id, value)
                        }}
                      />
                    )}
                  />
                </Labeled>
              </Grid>
              <Grid item>
                <Labeled label="Amount Reconciled">
                  <CurrencyField record={invoice} source="amount_reconciled" />
                </Labeled>
              </Grid>
            </Grid>
            <Grid item container direction="row" justifyContent="flex-start" alignItems="center" spacing={8}>
              <Grid item>
                <Labeled label="Items">
                  <ArrayField record={invoice} source="items">
                    <Datagrid>
                      <CurrencyField source="amount" label="Amount" />
                      <TextField source="description" label="Description" />
                    </Datagrid>
                  </ArrayField>
                </Labeled>
              </Grid>
            </Grid>
            <Grid item>
              <Labeled label="Total due">
                <CurrencyField record={invoice} source="total" />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Balance">
                <CurrencyField record={invoice} source="balance" />
              </Labeled>
            </Grid>
            <Grid item>
              <Labeled label="Invoice notes">
                {
                  // @ts-ignore
                  <TextField record={invoice} fullWidth multiline source="internal_notes" />
                }
              </Labeled>
            </Grid>
            <Grid
              item
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={6}
            >
              <Grid container item>
                <FunctionField
                  {...props}
                  record={invoice}
                  render={(record: any) => {
                    return (
                      <div>
                        <Button
                          {...props}
                          label="Add notes"
                          basePath="/invoices"
                          onClick={() => redirect(`/invoices/${record.id}/notes/`)}
                        >
                          <EditIcon />
                        </Button>
                      </div>
                    )
                  }}
                />
              </Grid>
              <Grid item>
                <LabeledTypographyField
                  label="BOL submission method"
                  variant="body2"
                  value={BrokerSubmissionMethodMap[get(invoice, 'broker.bol_submission_method', 'N/A')]}
                />
              </Grid>
              {invoice.broker.bol_submission_method === BrokerSubmissionMethods.Email && (
                <Grid item>
                  <Labeled label="BOL contact email">
                    <TextField record={invoice} source="broker.bol_contact_email" />
                  </Labeled>
                </Grid>
              )}
              {invoice.broker.bol_submission_method === BrokerSubmissionMethods.CarrierPortal && (
                <Grid item>
                  <Labeled label="Carrier Portal Link">
                    <TextField record={invoice} source="broker.carrier_portal_link" />
                  </Labeled>
                </Grid>
              )}
              <Grid item>
                <Grid item>
                  <Labeled label="BOL submission notes">
                    {
                      <TextField
                        fullWidth
                        // @ts-ignore
                        multiline
                        record={invoice}
                        source="broker.submission_requirement_notes"
                      />
                    }
                  </Labeled>
                </Grid>
              </Grid>
              <Grid item>
                <Labeled label="Quickbooks id">
                  <TextField record={invoice} source="quickbooks_id" />
                </Labeled>
              </Grid>
              {invoice.status !== INVOICE_STATUS.paid && hasEditPermissions && (
                <Grid item>
                  <Labeled label="Invoice Transitions" fullWidth>
                    <FSMButtonToolbar
                      record={invoice}
                      recordType={FSM_TRANSITION_RECORD_TYPE.invoice}
                      classes={classes}
                      includeCancel={false}
                      isHidden={false}
                    />
                  </Labeled>
                </Grid>
              )}
              {(Object.keys(SUPPORTED_EDI_INVOICE_SHIPPER_FEINS).includes(record?.load?.source) ||
                Object.values(SUPPORTED_EDI_INVOICE_SHIPPER_FEINS).includes(
                  record?.load?.broker?.fein_number
                )) && (
                <Grid item>
                  <Labeled label="EDI Invoice Actions" fullWidth>
                    <BaseActionDetailButton
                      record={invoice}
                      actionRequest={sendEdiInvoiceActionRequest}
                      actionTitle={sendEdiInvoiceTitle}
                      actionText={sendEdiInvoiceBody}
                    />
                  </Labeled>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Divider style={{ marginTop: '40px' }} />
      </div>
    )
  })
  return (
    <div style={{ paddingTop: '20px' }}>
      {hasAddPermissions && (
        <div>
          <GenerateInvoiceButton redirect={redirect} {...props} />
        </div>
      )}
      <br />
      <br />
      {invoices}
    </div>
  )
}

const StatementButtonType = {
  edit: 'edit',
  show: 'show',
}

const StatementButton = ({ type, ...props }: any) => {
  const { record } = props
  const redirect = useRedirect()
  if (!record) {
    return null
  }
  let icon, label, redirectUrl: any, visible
  switch (type) {
    case StatementButtonType.edit:
      label = 'Edit'
      icon = <EditIcon />
      redirectUrl = `/statements/${record.id}/`
      visible = record.is_editable
      break
    case StatementButtonType.show:
      label = 'View'
      icon = <VisibilityIcon />
      redirectUrl = `/statements/${record.id}/show`
      visible = true
      break
    default:
      return null
  }
  return visible ? (
    <Button {...props} label={label} basePath="/statements" onClick={() => redirect(redirectUrl)}>
      {icon}
    </Button>
  ) : null
}

const JobShow = (props: any) => {
  const { permissions } = usePermissions()
  const { record: job } = useShowController(props)

  return (
    <CustomJobShow aside={<Aside />} permissions={permissions} {...props}>
      <TabbedShowLayout>
        <Tab label="Job Overview">
          <TextField source="load.contact_name" label="Load contact name" />
          <TextField source="load.contact_email" label="Load contact email" />
          <TextField source="load.contact_phone" label="Load contact phone" />
          <CustomLinkifiedField source="load.internal_notes" label="Internal load notes" />
          <FunctionField
            label="Job to recover"
            source="job_to_recover"
            render={(record: any) => {
              if (record?.job_to_recover) {
                return (
                  <ReferenceField
                    basePath="/jobs"
                    record={record}
                    source="job_to_recover.id"
                    reference="jobs"
                    link="show"
                  >
                    <TextField source="human_id" />
                  </ReferenceField>
                )
              }
            }}
          />
          <FunctionField
            label="Confirmed rate"
            source="confirmed_rate"
            render={(record: any) => {
              const confirmedRate = record.confirmed_rate
              if (confirmedRate) {
                const rate = formatTripRate(confirmedRate)
                return record.status === JobStatus.Pending ? `User entered: ${rate}` : rate
              }
            }}
          />
          <TextField source="rate_per_mile" />
          <FunctionField
            label="Listed Price"
            source="load.trip_rate"
            render={(record: any) => formatTripRate(record.load.trip_rate)}
          />
          <NumberField source="load.trip_distance_mi" label="Trip distance (miles)" />
          <FunctionField
            label="Customer's Bid Range"
            render={(record: any) => {
              let bid_max = record.user_submitted_bid_max
              let bid_min = record.user_submitted_bid_min
              if (bid_max && bid_min) {
                return (
                  <span>
                    ${bid_min} - ${bid_max}
                  </span>
                )
              }
              if (record.load.trip_rate) {
                return (
                  <span>Booking requested with listed price {formatTripRate(record.load.trip_rate)}</span>
                )
              }
              return null
            }}
          />
          <FunctionField
            label="CT Estimate"
            render={(record: any) => {
              let estimatedRate = record.load.estimated_rate
              if (estimatedRate) {
                return <span>${estimatedRate}</span>
              }
              return null
            }}
          />
          <FunctionField
            label="CT Estimate Range"
            render={(record: any) => {
              let estimatedRateMin = record.load.estimated_rate_min
              let estimatedRateMax = record.load.estimated_rate_max
              if (estimatedRateMin && estimatedRateMax) {
                return (
                  <span>
                    ${estimatedRateMin} - ${estimatedRateMax}
                  </span>
                )
              }
              return null
            }}
          />
          <NumberField source="load.truck_weight_lb" label="Load weight (lbs)" />
          <NumberField source="load.truck_length_ft" label="Truck length (ft)" />
          <TextField source="load.equipment" label="Trailer type" />
          <CustomBooleanField source="load.is_load_out_trailer" label="Load out trailer" />
          <TextField source="booking_type" />
          <TextField source="broker_policy" />
          <FunctionField
            label="Created by"
            render={(record: any) => <UserLink user={{ id: record.created_by }} />}
          />
          <TextField source="id" />
          <TextField source="notes" />
          <DateField source="date_created" options={{ timeZoneName: 'short' }} />
          <DateField source="date_updated" options={{ timeZoneName: 'short' }} />
          <CustomBooleanField source="is_auto_approved" label="Auto-approved" />
        </Tab>
        <Tab label="Route progress" path="route">
          <TrackingSection {...props} />
        </Tab>
        <Tab label="Load stops" path="load">
          <ReferenceField source="load.id" reference="loads" label="Load id" link="show">
            <TextField source="id" />
          </ReferenceField>
          <TextField source="load.source" label="Source" />
          <TextField source="load.source_human_id" label="Broker load ID" />
          <TextField source="load.source_id" label="Source id" />
          <LoadStops source="load.stops" changeTrailerNumber={true} />
        </Tab>
        <Tab label="Documents" path="documents">
          <UploadDocumentButton permissions={permissions} />
          <ArrayField source="documents">
            <Datagrid>
              <TextField source="type" />
              <ArrayField source="attachments">
                <Datagrid>
                  <FileField source="file" title="original_filename" label="" />
                </Datagrid>
              </ArrayField>
              <DateField source="date_created" options={{ timeZoneName: 'short' }} showTime />
              <DateField source="date_updated" options={{ timeZoneName: 'short' }} showTime />
              <TextField source="description" />
              <FunctionField
                label="Uploaded by"
                render={(record: any) => <UserLink user={{ id: record.uploaded_by }} showEmail />}
              />
            </Datagrid>
          </ArrayField>
        </Tab>
        <Tab label="Statements" path="statements">
          <ArrayField source="statements">
            <Datagrid>
              <TextField source="type" />
              <TextField source="status" />
              <FunctionField
                label="Line items"
                render={(record: any) => {
                  return record.line_items.length === 0 ? (
                    <GenericFormField label="0.00" />
                  ) : (
                    <ArrayField record={record} source="line_items">
                      <Datagrid>
                        <TextField source="description" />
                        <TextField source="type" />
                        <TextField source="amount" />
                      </Datagrid>
                    </ArrayField>
                  )
                }}
              />
              <TextField source="subtotal" />
              <FunctionField
                label="Fee items"
                render={(record: any) => {
                  return record.fee_items.length === 0 ? (
                    <GenericFormField label="0.00" />
                  ) : (
                    <ArrayField record={record} source="fee_items">
                      <Datagrid>
                        <FunctionField
                          label="Description"
                          render={(record: any) => {
                            if (record.optional_policy_id) {
                              const paymentLink = getPaymentPolicyLinkForId(record.optional_policy_id)
                              return <a href={paymentLink}>{record.description}</a>
                            } else {
                              return <TextField source="description" />
                            }
                          }}
                        />
                        <TextField source="amount" />
                        <TextField source="type" />
                      </Datagrid>
                    </ArrayField>
                  )
                }}
              />
              <TextField source="lease_fee" />
              <TextField source="service_fee" />
              <TextField source="dispatch_service_fee" />
              <TotalCashAdvancePayments label="Total cash advance payments" />
              <TextField source="total" />
              <FunctionField
                render={(record: any) => {
                  return record.type === StatementType.JobCorrection ? (
                    <CorrectionFinalizeButton permissions={permissions} record={{ ...record, job }} />
                  ) : null
                }}
              />
              <StatementButton type={StatementButtonType.edit} />
              <StatementButton type={StatementButtonType.show} />
            </Datagrid>
          </ArrayField>
        </Tab>
        <Tab label="Invoices" path="invoices">
          <InvoiceSection permissions={permissions} />
        </Tab>
        <Tab label="Issues" path="issues">
          <IssueSectionV2 permissions={permissions} />
        </Tab>
        <Tab label="Checkins" path="checkins">
          <ArrayField source="checkins">
            <Datagrid>
              <TextField source="stop.sequence_number" label="Stop #" />
              <TextField source="stop.type_detail" label="Stop type detail" />
              <TextField source="stop.location_city" label="Stop location" />
              <TextField source="trigger" />
              <TextField source="type" />
              <TextField source="localized_location_timestamp" label="Timestamp" />
              <TextField source="stop_appointment_window" />
              <NumberField source="location_lat" />
              <NumberField source="location_long" />
              <NumberField source="location_accuracy" />
            </Datagrid>
          </ArrayField>
        </Tab>
        <Tab label="Zendesk tickets" path="zendesk-tickets">
          <FunctionField
            label="Zendesk tickets possibly associated with this job"
            render={(record: any) => {
              return record ? <JobZendeskTickets job={record} /> : null
            }}
          />
        </Tab>
      </TabbedShowLayout>
    </CustomJobShow>
  )
}

const ParsingStatusDropdown = styled(Dropdown)`
  .dropdown-select {
    min-width: 180px;
    padding: 0px;
    height: 40px;

    div {
      font-size: 0.875rem !important;
      font-weight: 400;
    }
  }
`

export default withStyles(styles)(JobShow)
